import { useStore } from 'react-admin';

/**
 * Profil bebe courant.
 *
 * Presque toutes les ressources de l'API sont portees par un profil bebe : leur
 * chemin est `/babies/{babyId}/…`. React Admin n'a pas de notion de portee — il
 * suppose une ressource joignable par son nom seul. Ce module comble l'ecart avec
 * une selection unique, persistee, lue par le `dataProvider` a chaque appel.
 *
 * `useStore` de React Admin est prefere a un store maison : la valeur est
 * persistee et **tous les abonnes sont notifies** du changement, ce qui rafraichit
 * les listes ouvertes sans rechargement de page.
 *
 * La valeur vit hors de React (`currentBabyIdRef`) parce que le `dataProvider`
 * n'est pas un composant et ne peut pas appeler de hook. Les deux representations
 * sont tenues synchrones par `setBabyId`, seul point d'ecriture.
 */

export const BABY_SCOPE_STORE_KEY = 'miamiam.babyScope';

/**
 * Derniere valeur connue, lisible depuis le `dataProvider`.
 *
 * Initialisee depuis `localStorage` au chargement du module : sans cela, le
 * premier appel de donnees suivant un rechargement partirait sans profil et
 * echouerait, alors que la selection est bien persistee.
 */
let currentBabyId: string | null = null;

const readPersisted = (): string | null => {
  try {
    const raw = globalThis.localStorage.getItem(`RaStore.${BABY_SCOPE_STORE_KEY}`);

    if (raw === null) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);

    return typeof parsed === 'string' && parsed !== '' ? parsed : null;
  } catch {
    // Stockage indisponible ou contenu corrompu : on repart d'une absence de
    // selection, que l'interface signale explicitement.
    return null;
  }
};

currentBabyId = readPersisted();

/** Lue par le `dataProvider`, qui ne peut pas appeler de hook. */
export const currentBabyIdRef = {
  get: (): string | null => currentBabyId,
  set: (value: string | null): void => {
    currentBabyId = value === '' ? null : value;
  },
};

export interface BabyScope {
  readonly babyId: string | null;
  readonly setBabyId: (babyId: string | null) => void;
}

/**
 * Selection courante et son setter.
 *
 * Le setter met a jour les deux representations dans le meme appel : le store
 * React Admin, pour l'interface, et la reference hors React, pour le
 * `dataProvider`. Les separer laisserait une fenetre pendant laquelle une liste
 * se rafraichit avec l'ancien profil.
 */
export const useBabyScope = (): BabyScope => {
  const [babyId, setStoredBabyId] = useStore<string | null>(BABY_SCOPE_STORE_KEY, null);

  return {
    babyId: babyId === '' ? null : babyId,
    setBabyId: (next: string | null): void => {
      currentBabyIdRef.set(next);
      setStoredBabyId(next);
    },
  };
};
